import { generateClientId } from "../../utils/clientId";

const DB_NAME = "suivi-qualite-livreur";
const DB_VERSION = 1;
const UPLOAD_STORE = "deliveryUploads";
const CACHE_STORE = "deliveryCache";

// Jest/jsdom and a few restrictive browsers do not expose IndexedDB. The
// in-memory fallback keeps the UI usable, while `persistent: false` lets the
// caller warn that closing the page would lose the draft.
const memoryUploads = new Map();
const memoryCache = new Map();

function hasIndexedDb() {
    return typeof globalThis.indexedDB !== "undefined";
}

function openDatabase() {
    return new Promise((resolve, reject) => {
        const request = globalThis.indexedDB.open(DB_NAME, DB_VERSION);
        let settled = false;
        const timeout = setTimeout(() => {
            if (settled) return;
            settled = true;
            reject(new Error("IndexedDB ne répond pas"));
        }, 4000);
        const fail = error => {
            if (settled) return;
            settled = true;
            clearTimeout(timeout);
            reject(error);
        };
        request.onupgradeneeded = () => {
            const db = request.result;
            if (!db.objectStoreNames.contains(UPLOAD_STORE)) {
                const store = db.createObjectStore(UPLOAD_STORE, { keyPath: "id" });
                store.createIndex("ownerKey", "ownerKey", { unique: false });
            }
            if (!db.objectStoreNames.contains(CACHE_STORE)) {
                db.createObjectStore(CACHE_STORE, { keyPath: "ownerKey" });
            }
        };
        request.onsuccess = () => {
            if (settled) {
                request.result.close();
                return;
            }
            settled = true;
            clearTimeout(timeout);
            resolve(request.result);
        };
        request.onerror = () => fail(request.error || new Error("IndexedDB indisponible"));
        request.onblocked = () => fail(new Error("IndexedDB est bloqué par un autre onglet"));
    });
}

async function runTransaction(storeName, mode, operation) {
    const db = await openDatabase();
    try {
        return await new Promise((resolve, reject) => {
            const transaction = db.transaction(storeName, mode);
            const request = operation(transaction.objectStore(storeName));
            let result;
            request.onsuccess = () => { result = request.result; };
            request.onerror = () => reject(request.error || new Error("Stockage local indisponible"));
            transaction.oncomplete = () => resolve(result);
            transaction.onabort = () => reject(transaction.error || new Error("Stockage local interrompu"));
            transaction.onerror = () => reject(transaction.error || new Error("Stockage local indisponible"));
        });
    } finally {
        db.close();
    }
}

function createId() {
    return generateClientId("delivery");
}

function storedFile(file, index) {
    return {
        blob: file,
        name: file?.name || `bon-livraison-${index + 1}.jpg`,
        type: file?.type || "image/jpeg",
        lastModified: file?.lastModified || Date.now(),
    };
}

export function buildDeliveryOwnerKey(user, pharmacyId) {
    return `${user?.email || user?.username || "anonymous"}:${pharmacyId || "default"}`;
}

export async function enqueueDeliveryUpload({ files = [], ...payload }) {
    const upload = {
        ...payload,
        id: createId(),
        createdAt: new Date().toISOString(),
        attempts: 0,
        status: "pending",
        files: files.map((file, index) => storedFile(file, index)),
    };

    if (!hasIndexedDb()) {
        memoryUploads.set(upload.id, upload);
        return { ...upload, persistent: false };
    }

    await runTransaction(UPLOAD_STORE, "readwrite", store => store.put(upload));
    globalThis.navigator?.storage?.persist?.().catch(() => false);
    return { ...upload, persistent: true };
}

export async function listDeliveryUploads(ownerKey) {
    if (!hasIndexedDb()) {
        return [...memoryUploads.values()]
            .filter(upload => upload.ownerKey === ownerKey)
            .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
    }

    const all = await runTransaction(UPLOAD_STORE, "readonly", store => store.getAll());
    return (all || [])
        .filter(upload => upload.ownerKey === ownerKey)
        .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

export async function removeDeliveryUpload(id) {
    if (!hasIndexedDb()) {
        memoryUploads.delete(id);
        return;
    }
    await runTransaction(UPLOAD_STORE, "readwrite", store => store.delete(id));
}

export async function updateDeliveryUpload(upload) {
    if (!hasIndexedDb()) {
        memoryUploads.set(upload.id, upload);
        return;
    }
    await runTransaction(UPLOAD_STORE, "readwrite", store => store.put(upload));
}

export async function saveDeliveryCache(ownerKey, deliveries) {
    const value = { ownerKey, deliveries, savedAt: new Date().toISOString() };
    if (!hasIndexedDb()) {
        memoryCache.set(ownerKey, value);
        return;
    }
    await runTransaction(CACHE_STORE, "readwrite", store => store.put(value));
}

export async function readDeliveryCache(ownerKey) {
    if (!hasIndexedDb()) return memoryCache.get(ownerKey)?.deliveries || [];
    const value = await runTransaction(CACHE_STORE, "readonly", store => store.get(ownerKey));
    return value?.deliveries || [];
}

export function createDeliveryUploadFormData(upload) {
    const formData = new FormData();
    (upload.files || []).forEach(file => {
        const blob = file?.blob || file;
        const name = file?.name || blob?.name || "bon-livraison.jpg";
        if (typeof File !== "undefined" && blob instanceof File) formData.append("photos", blob);
        else formData.append("photos", blob, name);
    });
    const first = upload.files?.[0];
    if (first) {
        const blob = first?.blob || first;
        const name = first?.name || blob?.name || "bon-livraison.jpg";
        if (typeof File !== "undefined" && blob instanceof File) formData.append("photo", blob);
        else formData.append("photo", blob, name);
    }
    if (upload.kind === "confirm") {
        formData.append("nombreCaisses", String(upload.nombreCaisses));
    }
    return formData;
}

export function isRetryableUploadError(error) {
    const status = error?.response?.status;
    return !status || status === 408 || status === 429 || status >= 500;
}

function sameDelivery(delivery, snapshot) {
    if (snapshot?.deliveryHistoryId != null && delivery?.deliveryHistoryId != null) {
        return String(snapshot.deliveryHistoryId) === String(delivery.deliveryHistoryId);
    }
    return snapshot?.ehpadId != null && String(snapshot.ehpadId) === String(delivery?.ehpadId);
}

export function mergeQueuedDeliveries(deliveries, uploads) {
    const merged = (deliveries || []).map(delivery => ({ ...delivery }));
    (uploads || []).forEach(upload => {
        const snapshot = upload.ehpad || {};
        let delivery = merged.find(item => sameDelivery(item, snapshot));
        if (!delivery) {
            delivery = { ...snapshot };
            merged.push(delivery);
        }
        delivery.pendingSync = true;
        delivery.pendingSyncCount = (delivery.pendingSyncCount || 0) + 1;
        delivery.syncBlocked = delivery.syncBlocked || upload.status === "blocked";
        if (upload.kind === "confirm") delivery.delivered = true;
    });
    return merged;
}
